
import { ref, onMounted, onBeforeUnmount, computed, defineComponent, onBeforeMount } from "vue";
import { Constants } from "@app/constants";
import { CurrentUser } from "@utils/user";
import { Emitter } from "@utils/events";
import { debounce } from "throttle-debounce";

export default defineComponent({
  name: "CiMediaConsent",
  props: {
    source: {
      type: String,
      required: true,
      validator: (src: string) => ["BigMarker", "Infogram", "SoundCloud", "Vimeo", "Youtube"].includes(src),
    },
    sourceMediaId: {
      type: String,
      required: true,
    },
    sourceMediaTitle: {
      type: String,
      default: "",
    },
  },
  setup(props) {
    const consentGiven = ref(false);
    const infogramEmbed = ref<HTMLDivElement | null>(null);
    const infogramResized = ref(false);

    const klaroWatcher: KlaroWatcher = {
      update: (_manager, name, data) => {
        if (name !== "consents") return;

        consentGiven.value = data[consentName.value] || false;
        if (consentName.value === "infogram") {
          window.setTimeout(handleInfogram, Constants.defaultDelay);
        }
      },
    };

    const embedClasses = computed(() => {
      const classes = [];

      if (props.source !== "SoundCloud" && !infogramResized.value) {
        classes.push("embed-responsive-16by9");
      }

      return classes;
    });

    const consentName = computed(() => {
      if (["BigMarker", "Infogram", "SoundCloud", "Vimeo", "Youtube"].includes(props.source))
        return props.source.toLowerCase();

      throw Error(`Unexpected source ${props.source}`);
    });

    const giveConsent = () => {
      window.klaro?.getManager()?.updateConsent(consentName.value, true);
      window.klaro?.getManager()?.applyConsents();
      window.klaro?.getManager()?.saveConsents();
    };

    onBeforeMount(() => {
      if (CurrentUser.signedIn) {
        consentGiven.value = true;
      } else {
        const klaroManager = window.klaro?.getManager();
        consentGiven.value = klaroManager?.getConsent(consentName.value) === true;
        klaroManager?.applyConsents();
        klaroManager?.watch(klaroWatcher);
      }
    });

    onMounted(() => {
      if (consentName.value === "infogram") {
        window.setTimeout(handleInfogram, Constants.defaultDelay);
      }
      Emitter.on("ci::page::resized", async () => {
        debouncedResize();
      });
    });

    onBeforeUnmount(() => {
      window.klaro?.getManager()?.unwatch(klaroWatcher);

      // remove possibly dangling timeouts
      const highestId = window.setTimeout(() => {
        for (let i = highestId; i >= 0; i--) {
          window.clearTimeout(i);
        }
      }, 0);

      Emitter.off("ci::page::resized");
    });

    const handleInfogram = () => {
      window.InfogramEmbeds.process();
      window.setTimeout(infogramResize, Constants.defaultDelay);
    };

    const infogramResize = () => {
      // on unmount this can get undefined
      if (!infogramEmbed.value) return;

      const iframe = infogramEmbed.value.firstElementChild as HTMLIFrameElement;
      if (infogramEmbed.value.dataset.processed === "1" && iframe && iframe.style.height !== "130px") {
        infogramEmbed.value.style.minHeight = iframe.style.height;
        infogramResized.value = true;
      } else {
        window.setTimeout(infogramResize, Constants.defaultDelay);
      }
    };

    const debouncedResize = debounce(100, infogramResize);

    return {
      consentGiven,
      embedClasses,
      giveConsent,
      infogramEmbed,
    };
  },
});
